import { CellType } from "@jupyterlab/nbformat";
import { EditorType } from "@kaggle/sdk/kernels";
import { ThemeName } from "@kaggle/core";
import Postmate from "postmate";
import { JupyterSource } from "../../session/JupyterSource";
import { CodeLocation } from "../../types/CodeLocation";

/** Adds async method support to a frame channel. */
const decorateWithAsync = (child: any) => {
  child.callAsync = async (fname: string, data: any) => {
    child.call(fname, data || null);
    const res = await child.get(fname);
    if (res instanceof Error) {
      throw res;
    }
    return res;
  };
  return child;
};

/**
 * The client exposing inbound methods for the editor iframe.
 * Both of the Postmate models must implement all of the
 * calls defined here.
 *
 * Calls to frameChannel.call("methodName") will execute the
 * method named methodName defined inside:
 *  parentChannelPromise = new Postmate.Model(...)
 * in the bootstrap[JuptyerLab]SessionlessEditor files.
 */
export class EditorFrameClient {
  private connectResolve: any = null;
  private connectPromise = new Promise(
    resolve => (this.connectResolve = resolve)
  );
  private frameChannel: any;

  get frame() {
    return this.frameChannel?.frame;
  }

  async connect(frameContainer: HTMLElement, url: string) {
    // Ensure any previous channels are destroyed.
    this.destroy();

    this.frameChannel = await new Postmate({
      container: frameContainer,
      url,
    });
    decorateWithAsync(this.frameChannel);
    this.connectResolve();
    return this.frameChannel;
  }

  async initialize() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("initialize");
  }

  async connectKernel(proxyUrl: string, kernelName: string) {
    await this.ensureConnected();

    return this.frameChannel.callAsync("connectKernel", {
      proxyUrl,
      kernelName,
    });
  }

  async shutdownKernel() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("shutdownKernel");
  }

  async lockEditor() {
    await this.ensureConnected();
    this.frameChannel.call("lockEditor");
  }

  async dispatch(action: any) {
    await this.ensureConnected();
    this.frameChannel.call("dispatch", action);
  }

  async focusActiveCell() {
    await this.ensureConnected();
    this.frameChannel.call("focusActiveCell");
  }

  async on(type: string, handler: (arg: any) => any) {
    await this.ensureConnected();
    this.frameChannel.on(type, handler);
  }

  async setContent(content: string) {
    await this.ensureConnected();
    this.frameChannel.call("setContent", content);
  }

  async setTheme(themeName: ThemeName): Promise<ThemeName | undefined> {
    await this.ensureConnected();
    return this.frameChannel.callAsync("setTheme", themeName);
  }

  async setSourceType(sourceType: EditorType) {
    await this.ensureConnected();
    return this.frameChannel.callAsync("setSourceType", sourceType);
  }

  async undoCellDelete() {
    await this.ensureConnected();
    this.frameChannel.call("undoCellDelete");
  }

  // ########## JupyterHandler methods ##########
  // NOTE(herbison): call/callAsync MUST match up on both sides of the Postmate model.
  // If inside bootstrap you create an async method, you must use callAsync, and vice versa.
  // This also must be consistent across the two bootstraps (vanilla and lab) since we have
  // one client for both implementations.
  // If they don't match, you will see strange behavior, like seeing the method called twice.

  async setSource(content: JupyterSource) {
    await this.ensureConnected();
    return this.frameChannel.callAsync("setSource", content.serialize());
  }

  async getSource() {
    const raw = await this.getRAWSource();
    return JupyterSource.fromNotebookContent(JSON.parse(raw));
  }

  async getRAWSource() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("getSource");
  }

  async getSourceWithOutputs() {
    const raw = await this.getRAWSourceWithOutputs();
    return JupyterSource.fromNotebookContent(JSON.parse(raw));
  }

  async getRAWSourceWithOutputs() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("getSourceWithOutputs");
  }

  async deleteCell() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("deleteCell");
  }

  async execute(code: string) {
    await this.ensureConnected();
    return this.frameChannel.callAsync("execute", code);
  }

  async executeAll() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("executeAll");
  }

  async executeSelected() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("executeActiveCell");
  }

  async executeCellsAbove() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("executeCellsAbove");
  }

  async executeCellsBelow() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("executeCellsBelow");
  }

  async executeSelectedCode() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("executeSelectedCode");
  }

  async cutCell() {
    await this.ensureConnected();
    return this.frameChannel.call("cutCell");
  }

  async copyCell() {
    await this.ensureConnected();
    return this.frameChannel.call("copyCell");
  }

  async pasteCell() {
    await this.ensureConnected();
    return this.frameChannel.call("pasteCell");
  }

  async insertCell(type: CellType) {
    await this.ensureConnected();
    return this.frameChannel.callAsync("insertCell", type);
  }

  async moveCells(direction: string) {
    await this.ensureConnected();
    return this.frameChannel.callAsync("moveCells", direction);
  }

  async stopExecution() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("stopExecution");
  }

  async restartKernel() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("restartKernel");
  }

  async toCode() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("toCode");
  }

  async toMarkdown() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("toMarkdown");
  }

  async toggleShowLineNumbers() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("toggleShowLineNumbers");
  }

  async collapseAllCodeCells() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("collapseAllCodeCells");
  }

  async expandAllCodeCells() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("expandAllCodeCells");
  }

  async toggleOutputScrolling() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("toggleOutputScrolling");
  }

  async toggleWordWrap() {
    await this.ensureConnected();
    return this.frameChannel.callAsync("toggleWordWrap");
  }

  async runCommand(cmd: string) {
    await this.ensureConnected();
    return this.frameChannel.callAsync("runCommand", cmd);
  }

  async navigateToCode(codeLocation: CodeLocation) {
    await this.ensureConnected();
    return this.frameChannel.callAsync("navigateToCode", codeLocation);
  }

  destroy() {
    this.frameChannel?.destroy();
  }

  private async ensureConnected() {
    await this.connectPromise;
  }
}

/** The singleton-ish instance of the frame client. */
export const editorFrameClient = new EditorFrameClient();
